import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Card } from "@/components/ui/card";

import type { DayPoint } from "../api";

/**
 * People and opens per day across the window.
 *
 * Two series on purpose. Opens alone can't distinguish "four people looked
 * once" from "one person refreshed four times", and for a tool with a dozen
 * users that's the difference between adoption and one person's habit. People
 * is the line you actually judge health by; opens is the shaded context
 * behind it.
 *
 * Quiet days are zero-filled server-side rather than omitted — a gap in the
 * axis is the signal, and joining Friday to Monday would hide a dead weekend.
 */
export function UsageTimeline({ daily }: { daily: DayPoint[] }) {
  if (daily.length === 0) return null;

  const data = daily.map((d) => ({
    ...d,
    label: shortDay(d.day),
  }));
  const peakUsers = Math.max(1, ...daily.map((d) => d.users));
  const peakEvents = Math.max(1, ...daily.map((d) => d.events));

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Daily activity
        </h2>
        <span className="text-xs text-muted-foreground">
          People (line) and opens (shaded)
        </span>
      </div>
      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {/* ComposedChart, not AreaChart: an AreaChart silently ignores a
              <Line> child, so the people series rendered as nothing at all and
              the panel showed only opens. */}
          <ComposedChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
            <defs>
              <linearGradient id="ph-opens" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--cinch-brand))" stopOpacity={0.28} />
                <stop offset="100%" stopColor="hsl(var(--cinch-brand))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              className="text-muted-foreground"
              axisLine={false}
              tickLine={false}
            />
            {/* Two axes, because the series differ by an order of magnitude:
                sharing one squashes people (single digits) into a flat line
                against opens (tens). People owns the left axis — it's the
                number you judge adoption by. */}
            <YAxis
              yAxisId="people"
              allowDecimals={false}
              domain={[0, Math.max(2, peakUsers + 1)]}
              tick={{ fontSize: 11 }}
              className="text-muted-foreground"
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <YAxis
              yAxisId="opens"
              orientation="right"
              allowDecimals={false}
              domain={[0, Math.max(2, peakEvents + 1)]}
              tick={{ fontSize: 11 }}
              className="text-muted-foreground"
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip
              contentStyle={{
                fontSize: 12,
                borderRadius: 8,
                border: "1px solid hsl(var(--border))",
                background: "hsl(var(--card))",
              }}
              labelFormatter={(l) => String(l)}
              formatter={(value: number, name: string) => [
                value,
                name === "users" ? "people" : "opens",
              ]}
            />
            <Area
              yAxisId="opens"
              type="monotone"
              dataKey="events"
              stroke="hsl(var(--cinch-brand))"
              strokeWidth={1}
              fill="url(#ph-opens)"
            />
            <Line
              yAxisId="people"
              type="monotone"
              dataKey="users"
              stroke="hsl(var(--cinch-power))"
              strokeWidth={2}
              dot={{ r: 2 }}
              activeDot={{ r: 4 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

/** "2026-08-11" → "Aug 11", built in local time to avoid a UTC day-shift. */
function shortDay(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
